/**
 * FlowPayView — halaman bayaran AWAM untuk Akaun Flow Ultra (RM100 / 30 hari).
 *
 * FASA 0 — paling ringkas. Tujuannya satu: pelanggan boleh bayar hari ini melalui
 * Billplz, dan sistem merekod emel + butiran bayaran + tarikh tamat. Slot, kredential
 * dalam app dan reminder automatik menyusul (PLAN_FLOW_ULTRA.md).
 *
 * TIADA LOG MASUK DIPERLUKAN — ini disengajakan. Pelanggan Flow sedia ada
 * kebanyakannya bukan pengguna SESAAT, dan mereka memang tidak boleh mendaftar
 * (butang Daftar dilumpuhkan tanpa kod ejen). Kalau kebetulan mereka log masuk,
 * uid tetap direkod pada order.
 *
 * Kredential akaun Google masih dihantar MANUAL melalui WhatsApp selepas bayaran.
 * Halaman ini berkata begitu dengan jelas — jangan janji apa yang belum wujud.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { CheckCircle2, Loader2, Clock, XCircle, ShieldCheck } from 'lucide-react';

import '../design/tokens.css';
import '../design/ui.css';

/* Harga + tempoh SEBENAR datang dari server (callable getFlowProdukJualan →
   produk "Sold on Billplz" di page admin Flow → Products). Nilai ini = sandaran
   bila callable belum dideploy — sama dengan sandaran server (RM100 / 30 hari).
   ⚠️ KEMBAR: functions/flowLangganan.js FLOW_HARGA_LALAI_SEN / FLOW_HARI_LALAI. */
const PRODUK_LALAI = { name: 'Akaun Flow Ultra', priceSen: 10000, days: 30 };
const rmTeks = (sen) => (sen % 100 ? (sen / 100).toFixed(2) : String(sen / 100));
const WHATSAPP_ADMIN = '601135011231';

/* Gaya halaman — dalam <style> dan BUKAN inline, sebab style inline tidak boleh
   memegang media query. Itulah punca sebenar aduan "tak nampak jelas di telefon":
   rangka asal guna lajur tetap 236px, jadi pada skrin 375px kad bayaran tinggal
   ~99px lebar. Sebarang lebar tetap di sini MESTI ada pasangan media query. */
const FlowStyles = () => (
    <style>{`
@keyframes s-spin-kf{to{transform:rotate(360deg)}}
.s-spin{animation:s-spin-kf 1s linear infinite}

.fu-frame{min-height:100vh;background:var(--s-ground);display:grid;grid-template-columns:236px minmax(0,1fr)}
.fu-side{border-right:1px solid var(--s-line);padding:16px 12px;display:flex;flex-direction:column;gap:18px;min-width:0}
.fu-brand{display:flex;align-items:center;gap:9px;padding:0 6px;min-width:0}
.fu-nav{display:flex;flex-direction:column;gap:2px;min-width:0}
.fu-acct{margin-top:auto;padding:0 6px;min-width:0}
.fu-acct-top{display:none}
.fu-main{overflow-y:auto;display:flex;align-items:flex-start;justify-content:center;padding:40px 20px;min-width:0}
.fu-card{width:100%;max-width:460px;background:var(--s-card);border:1px solid var(--s-line);
         border-radius:var(--s-r-lg);overflow:hidden;box-shadow:0 1px 2px rgba(0,0,0,.05)}
.fu-head{padding:26px 28px 20px;background:rgba(255,255,255,.029)}
.fu-body{padding:22px 28px 26px;border-top:1px solid var(--s-line)}
.fu-pad{padding:32px 28px;text-align:center}
.fu-shell{min-height:100vh;background:var(--s-ground);display:flex;align-items:center;
          justify-content:center;padding:32px 16px}
.fu-price{font-size:34px;font-weight:600;letter-spacing:-.02em}

@media (max-width:767px){
  /* Sidebar jadi bar atas — kandungan dapat lebar penuh skrin */
  .fu-frame{grid-template-columns:minmax(0,1fr);grid-template-rows:auto minmax(0,1fr)}
  .fu-side{border-right:none;border-bottom:1px solid var(--s-line);padding:11px 14px;gap:9px}
  .fu-nav{flex-direction:row;gap:6px;overflow-x:auto;scrollbar-width:none;-webkit-overflow-scrolling:touch}
  .fu-nav::-webkit-scrollbar{display:none}
  .fu-nav .s-navrow{width:auto;flex:0 0 auto;padding:0 12px}
  .fu-acct{display:none}                 /* dipindah ke bar atas */
  .fu-acct-top{display:block;margin-left:auto;font-size:12px;color:var(--s-ink-3);
               max-width:46%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .fu-main{padding:20px 14px 36px}
  .fu-shell{padding:20px 14px;align-items:flex-start}
  .fu-head{padding:20px 18px 16px}
  .fu-body{padding:18px 18px 24px}
  .fu-pad{padding:26px 18px}
  .fu-price{font-size:30px}
}
`}</style>
);

/* ─────────── Rangka aplikasi ───────────
   Halaman ini duduk SEBELUM dinding lesen, jadi ia tidak boleh guna shell utama
   App.jsx (shell itu dirender selepas gate). Rangka ringkas ini memberi rupa
   app.sesaat.ai yang sama — logo, menu, akaun — supaya pelanggan tahu dia berada
   dalam sistem, bukan di halaman bayar entah dari mana.

   Menu membawa ke halaman sebenar. Pelanggan yang belum melanggan SESAAT akan
   sampai ke skrin langganan — itu betul, bukan buntu. */
const NAV = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Workspace', path: '/workspace' },
    { label: 'Products', path: '/products' },
    { label: 'Akaun Flow', path: '/flow', on: true },
];

function AppFrame({ user, children }) {
    const navigate = useNavigate();
    return (
        <div className="s-root fu-frame">
            <FlowStyles />
            <aside className="fu-side">
                <div className="fu-brand">
                    <span style={{
                        width: 28, height: 28, borderRadius: 8, flex: 'none', display: 'grid', placeItems: 'center',
                        background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', fontWeight: 700, fontSize: 13,
                    }}>S</span>
                    <span style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-.01em' }}>SESAAT</span>
                    {/* Telefon: emel duduk di bar atas (blok bawah disembunyikan) */}
                    <span className="fu-acct-top">{user?.email || ''}</span>
                </div>

                <div className="fu-nav">
                    {NAV.map((n) => (
                        <button key={n.path} type="button"
                            className={`s-navrow${n.on ? ' s-navrow--on' : ''}`}
                            onClick={() => (n.on ? null : navigate(n.path))}>
                            {n.label}
                        </button>
                    ))}
                </div>

                <div className="fu-acct">
                    <p className="s-dim" style={{ marginBottom: 2 }}>Log masuk sebagai</p>
                    <p style={{ margin: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user?.email || '—'}
                    </p>
                </div>
            </aside>

            <div className="fu-main">{children}</div>
        </div>
    );
}

/* Mesej WhatsApp siap-isi — pelanggan tekan sekali, admin terus dapat semua
   maklumat yang dia perlukan untuk hantar kredential (rujukan order, emel,
   jumlah). Tanpa rujukan order, admin terpaksa mencari padanan sendiri. */
function waNotifyUrl({ orderId, email, amountSen, isRenewal }) {
    const rm = amountSen ? (amountSen / 100).toFixed(2) : '100.00';
    const msg = [
        'Salam 👋',
        '',
        `Saya sudah ${isRenewal ? 'menyambung' : 'membuat'} bayaran Akaun Flow Ultra.`,
        '',
        `• Jumlah: RM${rm}`,
        email ? `• E-mel akaun: ${email}` : null,
        orderId ? `• No. rujukan: ${orderId}` : null,
        '',
        'Mohon hantarkan butiran log masuk akaun Flow. Terima kasih!',
    ].filter(Boolean).join('\n');
    return `https://wa.me/${WHATSAPP_ADMIN}?text=${encodeURIComponent(msg)}`;
}

const labelStyle = { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--s-ink-2)', marginBottom: 6 };
const inputStyle = {
    width: '100%', height: 40, padding: '0 12px', borderRadius: 8,
    border: '1px solid var(--s-line-strong)', background: 'transparent',
    color: 'var(--s-ink)', fontFamily: 'inherit', fontSize: 14, outline: 'none',
};

/* ─────────── Halaman berjaya (/flow/berjaya) ─────────── */
export function FlowPaySuccessView({ user }) {
    const [params] = useSearchParams();
    const orderId = params.get('order');
    // Keadaan awal diterbitkan terus daripada orderId — jangan setState segerak
    // dalam badan effect, itu mencetuskan render berantai.
    const [state, setState] = useState(() => (orderId ? { phase: 'loading' } : { phase: 'notfound' }));

    useEffect(() => {
        if (!orderId) return undefined;
        let stopped = false;
        let tries = 0;

        const check = async () => {
            try {
                const fn = httpsCallable(functions, 'getFlowUltraOrderStatus');
                const { data } = await fn({ orderId });
                if (stopped) return;
                if (data.status === 'paid') { setState({ phase: 'paid', data }); return; }
                if (data.status === 'failed') { setState({ phase: 'failed' }); return; }
                // Webhook Billplz mungkin belum sampai — cuba semula beberapa kali.
                tries += 1;
                if (tries < 10) { setTimeout(check, 3000); setState({ phase: 'pending' }); }
                else setState({ phase: 'slow' });
            } catch {
                if (!stopped) setState({ phase: 'notfound' });
            }
        };
        check();
        return () => { stopped = true; };
    }, [orderId]);

    const wrap = (children) => (
        <div className="s-root fu-shell">
            <FlowStyles />
            <div className="fu-card fu-pad">{children}</div>
        </div>
    );

    if (state.phase === 'loading' || state.phase === 'pending') {
        return wrap(
            <>
                <Loader2 size={38} className="s-spin" style={{ color: 'var(--s-ink-3)', margin: '0 auto 16px', display: 'block' }} />
                <h1 className="s-title" style={{ marginBottom: 6 }}>Mengesahkan pembayaran…</h1>
                <p className="s-subtitle">Tunggu sekejap, jangan tutup halaman ini.</p>
            </>
        );
    }

    if (state.phase === 'paid') {
        const exp = state.data.expiresAt ? new Date(state.data.expiresAt) : null;
        return wrap(
            <>
                <CheckCircle2 size={44} style={{ color: 'var(--s-ok)', margin: '0 auto 16px', display: 'block' }} />
                <h1 className="s-title" style={{ marginBottom: 6 }}>Pembayaran diterima</h1>
                <p className="s-subtitle" style={{ marginBottom: 20 }}>
                    {state.data.isRenewal ? `Langganan anda telah disambung ${state.data.durationDays || 30} hari.` : `Langganan anda telah didaftarkan untuk ${state.data.durationDays || 30} hari.`}
                </p>
                {exp && (
                    <div style={{ border: '1px solid var(--s-line)', borderRadius: 10, padding: '12px 14px', marginBottom: 20 }}>
                        <p className="s-dim" style={{ marginBottom: 3 }}>Aktif sehingga</p>
                        <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                            {exp.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                )}
                <div style={{ background: 'var(--s-warn-soft)', borderRadius: 10, padding: '12px 14px', textAlign: 'left', marginBottom: 16 }}>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--s-warn)' }}>
                        <b>Langkah seterusnya:</b> tekan butang di bawah untuk maklumkan kami. Butiran log masuk
                        akaun Flow akan dihantar ke WhatsApp anda.
                    </p>
                </div>
                <a className="s-btn s-btn--primary" style={{ width: '100%', textDecoration: 'none' }}
                    href={waNotifyUrl({ orderId, email: user?.email, amountSen: state.data.amountSen, isRenewal: state.data.isRenewal })}
                    target="_blank" rel="noreferrer">
                    Maklumkan admin di WhatsApp
                </a>
                <p className="s-dim" style={{ marginTop: 10 }}>
                    Mesej sudah siap ditaip — anda cuma perlu tekan hantar.
                </p>
            </>
        );
    }

    if (state.phase === 'slow') {
        return wrap(
            <>
                <Clock size={40} style={{ color: 'var(--s-warn)', margin: '0 auto 16px', display: 'block' }} />
                <h1 className="s-title" style={{ marginBottom: 6 }}>Pembayaran sedang disahkan</h1>
                <p className="s-subtitle" style={{ marginBottom: 20 }}>
                    Bank mengambil masa lebih lama daripada biasa. Kalau duit sudah ditolak, langganan anda tetap
                    akan aktif — kami akan hubungi anda. Simpan nombor rujukan ini.
                </p>
                <p className="s-num" style={{ marginBottom: 20, wordBreak: 'break-all' }}>{orderId}</p>
                <a className="s-btn s-btn--primary" style={{ width: '100%', textDecoration: 'none' }}
                    href={waNotifyUrl({ orderId })} target="_blank" rel="noreferrer">
                    Maklumkan admin di WhatsApp
                </a>
            </>
        );
    }

    return wrap(
        <>
            <XCircle size={40} style={{ color: 'var(--s-stop)', margin: '0 auto 16px', display: 'block' }} />
            <h1 className="s-title" style={{ marginBottom: 6 }}>Pembayaran tidak selesai</h1>
            <p className="s-subtitle" style={{ marginBottom: 20 }}>Tiada apa-apa ditolak. Anda boleh cuba semula.</p>
            <a className="s-btn s-btn--primary" style={{ width: '100%', textDecoration: 'none' }} href="/flow">Cuba semula</a>
        </>
    );
}

/* ─────────── Halaman bayaran (/flow) ─────────── */
export default function FlowPayView({ user }) {
    const [phone, setPhone] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [produk, setProduk] = useState(null);
    const [produkRalat, setProdukRalat] = useState('');

    useEffect(() => {
        let batal = false;
        httpsCallable(functions, 'getFlowProdukJualan')()
            .then((r) => { if (!batal) setProduk(r.data); })
            .catch((e) => {
                if (batal) return;
                // Callable belum dideploy → harga lama (sama dengan yang server caj).
                if (e?.code === 'functions/not-found') setProduk(PRODUK_LALAI);
                else setProdukRalat(e?.message || 'Produk tidak dapat dimuatkan. Sila muat semula halaman.');
            });
        return () => { batal = true; };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        setBusy(true);
        try {
            // Emel & nama TIDAK dihantar — server ambil daripada token log masuk.
            // Emel ialah kunci dokumen langganan, jadi ia tidak boleh datang dari klien.
            const fn = httpsCallable(functions, 'createFlowUltraBill');
            const { data } = await fn({
                whatsappNumber: phone,
                returnOrigin: window.location.origin,
            });
            window.location.href = data.url;   // ke Billplz
        } catch (e2) {
            setErr(e2?.message || 'Gagal memulakan pembayaran. Sila cuba lagi.');
            setBusy(false);
        }
    };

    return (
        <AppFrame user={user}>
            <div className="fu-card">
                <div className="fu-head">
                    <h1 className="s-title" style={{ marginBottom: 6 }}>Akaun Flow Ultra</h1>
                    <p className="s-subtitle">Akaun Google Labs Flow untuk menjana gambar &amp; video AI.</p>

                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                        <span className="fu-price">{produk ? `RM${rmTeks(produk.priceSen)}` : '…'}</span>
                        <span className="s-subtitle">{produk ? `untuk ${produk.days} hari` : ''}</span>
                    </div>
                </div>

                <form onSubmit={submit} className="fu-body">
                    <div style={{ marginBottom: 16 }}>
                        <label style={labelStyle}>Akaun</label>
                        <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', color: 'var(--s-ink-2)' }}>
                            {user?.email || '—'}
                        </div>
                        <p className="s-dim" style={{ marginTop: 6 }}>
                            Langganan didaftarkan pada akaun ini. Guna akaun yang sama setiap kali menyambung.
                        </p>
                    </div>
                    <div style={{ marginBottom: 18 }}>
                        <label style={labelStyle} htmlFor="fu-phone">Nombor WhatsApp</label>
                        <input id="fu-phone" style={inputStyle} value={phone} required
                            onChange={(ev) => setPhone(ev.target.value)} placeholder="011-2345 6789" />
                        <p className="s-dim" style={{ marginTop: 6 }}>
                            Butiran log masuk akaun Flow dihantar ke nombor ini.
                        </p>
                    </div>

                    {(err || produkRalat) && (
                        <p style={{ fontSize: 13, color: 'var(--s-stop)', marginBottom: 14 }}>{err || produkRalat}</p>
                    )}

                    <button type="submit" className="s-btn s-btn--primary" style={{ width: '100%', height: 42 }} disabled={busy || !produk}>
                        {busy ? <><Loader2 size={15} className="s-spin" /> Menyediakan pembayaran…</> : (produk ? `Bayar RM${rmTeks(produk.priceSen)} melalui Billplz` : 'Memuatkan harga…')}
                    </button>

                    <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'flex-start' }}>
                        <ShieldCheck size={15} style={{ color: 'var(--s-ink-3)', flex: 'none', marginTop: 2 }} />
                        <p className="s-dim" style={{ margin: 0 }}>
                            Bayaran diproses oleh Billplz (FPX / kad). Ini bayaran sekali — tiada caj automatik
                            dikenakan pada akaun anda. Kami akan ingatkan sebelum tarikh tamat.
                        </p>
                    </div>
                </form>
            </div>
        </AppFrame>
    );
}
